/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

@file:Suppress("ktlint:standard:filename")

package systems.frictionless.impulse.browser.composable.question

import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import kotlinx.collections.immutable.persistentListOf
import org.jetbrains.compose.web.attributes.maxLength
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TextArea
import systems.frictionless.impulse.browser.composable.component.CenteredOnScreen
import systems.frictionless.impulse.browser.composable.component.Text
import systems.frictionless.impulse.browser.composable.component.WhiteBox
import systems.frictionless.impulse.browser.id.HtmlId
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.rest.client.model.Progress
import systems.frictionless.impulse.rest.model.Answer
import systems.frictionless.impulse.rest.model.Assessment
import systems.frictionless.impulse.rest.model.Question

@Composable
@Suppress("LongMethod")
fun Question(
    assessment: Assessment,
    question: Question,
    progress: Progress,
    showExtraInputFields: Boolean,
    onAnswerValueChanged: (Answer.Builder?) -> Unit
) {
    val (answerValue, setAnswerValue) = remember { mutableStateOf<Answer.Type?>(null) }
    val (extraText, setExtraText) = remember { mutableStateOf("") }

    CenteredOnScreen(classes = persistentListOf(AppStyleSheet.ignoreCenteringOnMobile)) {
        WhiteBox(classes = arrayOf(AppStyleSheet.fixedSize, AppStyleSheet.addHorizontalPaddingInMobileLandscape)) {
            ProgressInfo(progress)

            Div(attrs = {
                classes(AppStyleSheet.questionText)
                // Makes it easy to tests to verify that next question has loaded
                id(HtmlId.questionId(question.id))
            }) {
                Text(
                    value = question.text
                )
            }

            question.hint?.let { hint ->
                Div(attrs = {
                    classes(AppStyleSheet.questionHint)
                }) {
                    Text(value = hint)
                }
            }

            when (val questionType = question.type) {
                is Question.Type.Likert -> {
                    // Consider whether to report exception for bad answer cast
                    val castAnswerValue = answerValue as? Answer.Type.Likert
                    LikertFour(questionType, castAnswerValue, setAnswerValue)
                }
                is Question.Type.MultipleChoice -> {
                    // Consider whether to report exception for bad answer cast
                    val castAnswerValue = answerValue as? Answer.Type.MultipleChoice
                    MultipleChoice(questionType, castAnswerValue, setAnswerValue)
                }
                is Question.Type.FreeResponse -> {
                }
            }

            if (showExtraInputFields) {
                Div(attrs = {
                    classes(AppStyleSheet.questionTextArea)
                }) {
                    TextArea(
                        attrs = {
                            placeholder("Optional notes: Use this space if you feel like further clarification is needed.")
                            onInput {
                                setExtraText(it.value)
                            }
                            maxLength(Answer.MAX_COMMENT_LENGTH)
                        },
                        value = extraText
                    )
                }
            }

            if (answerValue != null) {
                onAnswerValueChanged(
                    Answer.Builder.new(
                        assessment,
                        question,
                        answerValue,
                        extraText
                    )
                )
            } else {
                onAnswerValueChanged(null)
            }
        }
    }
}

@Composable
private fun ProgressInfo(progress: Progress) {
    Div(attrs = {
        classes(AppStyleSheet.progress)
    }) {
        Span(attrs = {
            classes(AppStyleSheet.progressValue)
            id(HtmlId.questionIndex(progress.current))
        }) { Text((progress.current.value + 1).toString()) }
        Span(attrs = {
            classes(AppStyleSheet.progressTotal)
        }) { Text("/" + (progress.last.value + 1).toString()) }
    }
}
